<template>
    <BaseBoxSection :title="__('goods_services')">
        <template #tools>
            <div class="ibox-tools">
                <label v-if="showUnitPriceCalculation"><input
                    :checked="unitPriceCalculation"
                    :disabled="disabled || contractTypeName == 'invoicing_pl_VAT'"
                    type="checkbox"
                    @change="emitInput('unit_price_calculation', $event.target.checked)"
                > {{ __('unit_price_calculation') }}</label>

                <button-open-help-modal :helpName="helpPrefix + '-003'" :in-extra-div="false"/>
            </div>
        </template>


        <component
            :is="itemsSectionComponent"
            ref="itemSection"
            :disabled="disabled"
            :errors="errors"
            :items="items"
            :additionalFields="additionalFields"
            :gtus="gtus"
            :procedures="procedures"
            :requiredFields="requiredFields"
            :unitPriceCalculation="unitPriceCalculation"
            :permissionsAccess="permissionsAccess"
            :vatRates="vatRates"
            :isSymbolVisible="isSymbolVisible"
            :standardItems="standardItems"
            @addNewItem="$emit('addNewItem')"
            @hasExemptVat="$emit('hasExemptVat')"
            @inputs="emitInput($event.name, $event.value)"
            @itemChanged="$emit('itemChanged')"
            @itemInput="$emit('itemInput', $event)"
            @removeItem="$emit('removeItem', $event)"
            @addStandardItem="addStandardItem"
        />

        <div class="col-12">
            <div class="ibox-content">
                <div class="row m-b-sm">
                    <div :class="{'has-error': hasError('currency')}" class="col-md-2">
                        <label>{{ __('currency') }}{{ isRequiredDesignation('currency') }}</label>
                        <select2
                            :disabled="disabled || isConnectedToDocument"
                            :options="currenciesOptions"
                            :settings="currenciesSettings"
                            :value="currency"
                            @input="emitInput('currency', parseInt($event))"
                        ></select2>

                        <InputErrors :errors="getErrors('currency')"/>
                    </div>
                </div>
            </div>
        </div>
    </BaseBoxSection>
</template>

<script>
import BaseBoxSection from "../../BaseBoxSection";
import inputEmit from "../../../mixins/inputEmit";
import hasErrors from "../../../mixins/hasErrors";
import InputErrors from "../../form/InputErrors";
import ItemsSection from "./default/ItemsSection";
import MarginItemsSection from "./margin/MarginItemsSection";
import hasVatRates from "../../../mixins/hasVatRates";
import hasRequiredFields from "../../../mixins/hasRequiredFields";
import AddingItemsSection from "./adding/AddingItemsSection";
import hasPermissions from "../../../mixins/hasPermissions";

export default {
    name: "ItemsBox",
    components: {ItemsSection, MarginItemsSection, InputErrors, BaseBoxSection, AddingItemsSection},
    mixins: [inputEmit, hasErrors, hasVatRates, hasRequiredFields, hasPermissions],
    props: {
        items: {
            type: Array,
            required: true,
        },
        currencies: {
            type: Array,
            default: [],
        },
        showUnitPriceCalculation: {
            type: Boolean,
            default: false,
        },
        unitPriceCalculation: {
            type: Boolean,
            required: true,
        },
        currency: {
            type: Number,
            required: true,
        },
        isSelectedVies: {
            type: Boolean,
            required: true,
        },
        isConnectedToDocument: {
            type: Boolean,
            required: true,
        },
        contractTypeName: {
            type: String,
            required: true,
        },
        contractType: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        helpPrefix: {
            type: String,
            default: '',
        },
        isSymbolVisible: {
            type: Number,
            default: 0,
        },
        standardItems: {
            type: Array,
            default: [],
        },
        additionalFields: {
            type: Array,
            default: () => [],
        },
        gtus: {
            type: Array,
            default: () => [],
        },
        procedures: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        addStandardItem(index){
            this.$emit('addStandardItem', index)
        },
        initSummaries(summaries) {
            this.$refs.itemSection.initSummaries(summaries);
            this.$refs.itemSection.sumVatAmountAndEmitSummaries();
        },
        emitSummaries() {
            this.$refs.itemSection.emitSummaries();
        },
        handleNoContractorViesAlert() {
            if (this.hasViesWarning && this.contractType.country_code != null) {
                this.$store.dispatch('addAlert', {
                    name: 'no_contractor_vies',
                    alert: {
                        type: 'warning',
                        title: this.__('warning'),
                        msg: `${this.__('buyer_or_other_entity_vat_number_missing')} ${this.__('make_sure_it_is_possible')}`,
                    },
                });
            } else {
                this.$store.dispatch('removeIfExistAlert', 'no_contractor_vies');
            }
        },
    },
    computed: {
        itemsSectionComponent() {
            switch (this.contractTypeName) {
                case 'invoicing_pl_margin':
                    return 'MarginItemsSection';
                case 'adding':
                case 'adding_buy':
                case 'adding_sell':
                    return 'AddingItemsSection';
                default:
                    return 'ItemsSection';
            }
        },
        anyItemsSelectedUe() {
            for (let item of this.items) {
                let vatRate = this.getVatRate(parseInt(item.vat_rate));

                if (vatRate?.label === "NP. UE" || vatRate?.label === "0% WDT") {
                    return true;
                }
            }

            return false;
        },
        currenciesOptions() {
            return this.currencies.map(option => ({text: option.iso_code, id: option.id}));
        },
        currenciesSettings() {
            return {
                templateResult: (option) => {
                    let currency = this.currencies.find(c => c.id === parseInt(option.id));

                    if (currency === undefined) return option.text;
                    if (currency.is_trashed) return null;

                    return currency.iso_code + "; " + currency.name;
                },
            };
        },
        hasViesWarning() {
            return (this.anyItemsSelectedUe && !this.isSelectedVies);
        },
    },
    watch: {
        hasViesWarning() {
            this.handleNoContractorViesAlert();
        },
    },
};
</script>
